import React, { useState, useMemo } from 'react';
import { BarChart3, TrendingUp, Calendar, Filter, Layers, PieChart, Activity, Target, Zap, TrendingDown, Sparkles } from 'lucide-react';
import { CalculatedMetrics, GlobalAnalysisFilters } from '../types';
import { GlobalFilterPanel } from './ui/GlobalFilterPanel';
import { BiasSwitch } from './ui/BiasSwitch';
import { PerformancePerFasciaChart } from './charts/PerformancePerFasciaChart';
import { MarketCapAnalysisDashboard } from './charts/MarketCapAnalysisDashboard';
import { GapPerformanceHeatmap } from './charts/GapPerformanceHeatmap';
import { ComparativeBoxPlot } from './charts/ComparativeBoxPlot';
import { CorrelationMatrixHeatmap } from './charts/CorrelationMatrixHeatmap';
import { BestSetupsDashboard } from './charts/BestSetupsDashboard';
import { BucketAnalysisGrid } from './charts/BucketAnalysisGrid';
import { QuickInsights } from './analytics/QuickInsights';
import { SectionNavigator } from './analytics/SectionNavigator';
import { ChartExplainer } from './analytics/ChartExplainer';
import { InsightsSkeleton, ChartSkeleton, TableSkeleton } from './analytics/SkeletonLoader';
import { applyBiasToPriceBuckets, applyBiasToMarketCapBuckets, applyBiasToBestSetups } from '../utils/biasCalculations';
import { 
  applyGlobalFilters, 
  aggregateByPriceRange, 
  aggregateByMarketCap, 
  createGapPerformanceHeatmap,
  createBoxPlotData,
  calculateCorrelationMatrix,
  identifyBestSetups
} from '../utils/analysisCalculations';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

interface AnalyticsDashboardProps {
  data: CalculatedMetrics[];
}

export const AnalyticsDashboard: React.FC<AnalyticsDashboardProps> = ({ data }) => {
  const [activeTab, setActiveTab] = useState<'insights' | 'overview' | 'price' | 'marketcap' | 'correlations' | 'setups'>('insights');
  const [isLoading, setIsLoading] = useState(false);

  const [filters, setFilters] = useState<GlobalAnalysisFilters>({
    dateRange: [null, null],
    marketCapRange: [0, Infinity],
    gapPercentRange: [0, Infinity],
    priceRange: [0, Infinity],
    volumeThreshold: 0,
    closeType: 'all',
    pmhBreak: 'all',
    bias: 'long'
  });

  // Applica filtri ai dati
  const filteredData = useMemo(() => {
    setIsLoading(true);
    const result = applyGlobalFilters(data, filters);
    // Simula un breve caricamento per UX fluida
    setTimeout(() => setIsLoading(false), 300);
    return result;
  }, [data, filters]);

  // Calcola dati aggregati per i grafici e applica il bias
  const priceRangeData = useMemo(() => {
    const rawData = aggregateByPriceRange(filteredData);
    return applyBiasToPriceBuckets(rawData, filters.bias);
  }, [filteredData, filters.bias]);

  const marketCapData = useMemo(() => {
    const rawData = aggregateByMarketCap(filteredData);
    return applyBiasToMarketCapBuckets(rawData, filters.bias);
  }, [filteredData, filters.bias]);

  const heatmapData = useMemo(() => {
    return createGapPerformanceHeatmap(filteredData);
  }, [filteredData]);

  const boxPlotData = useMemo(() => {
    return {
      spike: createBoxPlotData(filteredData, 'spike'),
      volumeRatio: createBoxPlotData(filteredData, 'volumeRatio'),
      thirtyMinHigh: createBoxPlotData(filteredData, '30minHigh'),
      gap: createBoxPlotData(filteredData, 'gap')
    };
  }, [filteredData]);

  const correlationData = useMemo(() => {
    return calculateCorrelationMatrix(filteredData);
  }, [filteredData]);

  const bestSetupsData = useMemo(() => {
    const rawSetups = identifyBestSetups(filteredData);
    return applyBiasToBestSetups(rawSetups, filters.bias);
  }, [filteredData, filters.bias]);

  // Calcola statistiche per l'header
  const headerStats = useMemo(() => {
    if (filteredData.length === 0) {
      return {
        totalGaps: 0,
        dateRange: 'Nessun dato',
        activeFilters: 0
      };
    }

    const dates = filteredData.map(row => row.Date).sort((a, b) => a.getTime() - b.getTime());
    const startDate = dates[0];
    const endDate = dates[dates.length - 1];
    
    const activeFilters = [
      filters.dateRange[0] !== null || filters.dateRange[1] !== null,
      filters.marketCapRange[0] !== 0 || filters.marketCapRange[1] !== Infinity,
      filters.gapPercentRange[0] !== 0 || filters.gapPercentRange[1] !== Infinity,
      filters.priceRange[0] !== 0 || filters.priceRange[1] !== Infinity,
      filters.volumeThreshold !== 0,
      filters.closeType !== 'all',
      filters.pmhBreak !== 'all'
    ].filter(Boolean).length;

    return {
      totalGaps: filteredData.length,
      dateRange: `${format(startDate, 'dd MMM yyyy', { locale: it })} - ${format(endDate, 'dd MMM yyyy', { locale: it })}`,
      activeFilters
    };
  }, [filteredData, filters]);

  // Funzione per generare il riepilogo dei filtri attivi
  const getActiveFiltersText = () => {
    const activeFilters = [];

    if (filters.dateRange[0] || filters.dateRange[1]) {
      const startDate = filters.dateRange[0] ? format(filters.dateRange[0], 'dd/MM/yyyy', { locale: it }) : 'inizio';
      const endDate = filters.dateRange[1] ? format(filters.dateRange[1], 'dd/MM/yyyy', { locale: it }) : 'fine';
      activeFilters.push(`Periodo: ${startDate} - ${endDate}`);
    }

    if (filters.marketCapRange[0] !== 0 || filters.marketCapRange[1] !== Infinity) {
      const minCap = filters.marketCapRange[0] !== 0 ? `$${(filters.marketCapRange[0] / 1000000).toFixed(0)}M` : '$0';
      const maxCap = filters.marketCapRange[1] !== Infinity ? `$${(filters.marketCapRange[1] / 1000000).toFixed(0)}M` : '∞';
      activeFilters.push(`Market Cap: ${minCap} - ${maxCap}`);
    }

    if (filters.gapPercentRange[0] !== 0 || filters.gapPercentRange[1] !== Infinity) {
      const minGap = filters.gapPercentRange[0] !== 0 ? `${filters.gapPercentRange[0]}%` : '0%';
      const maxGap = filters.gapPercentRange[1] !== Infinity ? `${filters.gapPercentRange[1]}%` : '∞';
      activeFilters.push(`Gap: ${minGap} - ${maxGap}`);
    }

    if (filters.priceRange[0] !== 0 || filters.priceRange[1] !== Infinity) {
      const minPrice = filters.priceRange[0] !== 0 ? `$${filters.priceRange[0]}` : '$0';
      const maxPrice = filters.priceRange[1] !== Infinity ? `$${filters.priceRange[1]}` : '∞';
      activeFilters.push(`Prezzo: ${minPrice} - ${maxPrice}`);
    }

    if (filters.volumeThreshold !== 0) {
      const volume = filters.volumeThreshold >= 1000000 ? `${(filters.volumeThreshold / 1000000).toFixed(1)}M` : `${(filters.volumeThreshold / 1000).toFixed(0)}K`;
      activeFilters.push(`Volume PM min: ${volume}`);
    }

    if (filters.closeType !== 'all') {
      const closeText = filters.closeType === 'red' ? 'Solo chiusure rosse' : 'Solo chiusure verdi';
      activeFilters.push(closeText);
    }

    if (filters.pmhBreak !== 'all') {
      const pmhText = filters.pmhBreak === 'break' ? 'Solo PMH break' : 'Solo no PMH break';
      activeFilters.push(pmhText);
    }

    return activeFilters;
  };

  const activeFiltersText = getActiveFiltersText();

  // Configurazione delle tab
  const tabs = [
    {
      id: 'insights',
      label: 'Insight Rapidi',
      icon: Sparkles,
      description: 'Sintesi automatica dei pattern chiave'
    },
    {
      id: 'overview',
      label: 'Panoramica',
      icon: BarChart3,
      description: 'Analisi generale e bucket'
    },
    {
      id: 'price',
      label: 'Analisi Prezzo',
      icon: TrendingUp,
      description: 'Performance per fascia di prezzo'
    },
    {
      id: 'marketcap',
      label: 'Market Cap',
      icon: PieChart,
      description: 'Analisi per capitalizzazione'
    },
    {
      id: 'correlations',
      label: 'Correlazioni',
      icon: Activity,
      description: 'Heatmap e correlazioni'
    },
    {
      id: 'setups',
      label: 'Setup Ottimali',
      icon: Target,
      description: 'Configurazioni migliori'
    }
  ];

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId as typeof activeTab);
  };
  return (
    <div className="space-y-8 chart-animate-in">
      {/* Header Sezione */}
      <div className="premium-section-header text-center">
        <div className="flex items-center justify-center space-x-3 mb-4">
          <div className="premium-icon-bg">
            <BarChart3 className="h-8 w-8 text-blue-400" />
          </div>
          <h1 className="text-3xl font-bold text-white">Dashboard di Analisi Avanzata</h1>
        </div>
        <p className="text-lg text-gray-300 max-w-3xl mx-auto mb-6 leading-relaxed">
          Esplora pattern nascosti e correlazioni attraverso visualizzazioni interattive e analisi predittive
        </p>
        
        {/* Barra Statistiche Rapide */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="premium-stats-card">
            <div className="premium-icon-bg mb-3">
              <BarChart3 className="h-5 w-5 text-blue-400" />
            </div>
            <p className="premium-metric-label">Gap Analizzati</p>
            <p className="premium-metric-value">{headerStats.totalGaps.toLocaleString()}</p>
          </div>
          
          <div className="premium-stats-card">
            <div className="premium-icon-bg success mb-3">
              <Calendar className="h-5 w-5 text-green-400" />
            </div>
            <p className="premium-metric-label">Periodo Analisi</p>
            <p className="text-sm font-semibold text-white mt-2">{headerStats.dateRange}</p>
          </div>
          
          <div className="premium-stats-card">
            <div className="premium-icon-bg warning mb-3">
              <Filter className="h-5 w-5 text-yellow-400" />
            </div>
            <p className="premium-metric-label">Filtri Attivi</p>
            <p className="premium-metric-value">{headerStats.activeFilters}</p>
          </div>
        </div>
      </div>

      {/* Riepilogo Filtri Attivi */}
      {activeFiltersText.length > 0 && (
        <div className="premium-filter-panel">
          <div className="flex items-start space-x-3">
            <div className="premium-icon-bg">
              <Filter className="h-4 w-4 text-blue-400" />
            </div>
            <div className="flex-1">
              <h4 className="premium-chart-title text-sm mb-3">Filtri Applicati</h4>
              <div className="flex flex-wrap gap-2">
                {activeFiltersText.map((filterText, index) => (
                  <span
                    key={index}
                    className="filter-badge"
                  >
                    {filterText}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bias Switch */}
      <div className="flex justify-center">
        <BiasSwitch
          bias={filters.bias}
          onBiasChange={(newBias) => setFilters({ ...filters, bias: newBias })}
        />
      </div>

      {/* Pannello Filtri Globali */}
      <GlobalFilterPanel
        filters={filters}
        onFiltersChange={setFilters}
        totalResults={filteredData.length}
      />

      {/* Section Navigator */}
      <SectionNavigator
        sections={tabs}
        currentSection={activeTab}
        onNavigate={handleTabChange}
      />

      {/* Tab Navigation */}
      <div className="premium-tab-nav">
        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`premium-tab-button ${activeTab === tab.id ? 'active' : ''}`}
              title={tab.description}
            >
              <div className="flex items-center space-x-2">
                <tab.icon className="h-4 w-4" />
                <span className="hidden sm:inline">{tab.label}</span>
                <span className="sm:hidden">{tab.label.split(' ')[0]}</span>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center">
          <div className="premium-chart-card p-8">
            <div className="flex items-center space-x-3">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-400"></div>
              <span className="text-white font-medium">Aggiornamento analisi...</span>
            </div>
          </div>
        </div>
      )}
      {/* Sezione Grafici */}
      <div className="premium-chart-grid">
        {/* Insights Tab */}
        {activeTab === 'insights' && (
          <div className="space-y-8 chart-animate-in">
            {isLoading ? (
              <InsightsSkeleton />
            ) : (
              <>
                <QuickInsights data={filteredData} bias={filters.bias} />

                <ChartExplainer
                  title="Insight Rapidi"
                  explanation="Gli insight rapidi analizzano automaticamente il tuo dataset per identificare i pattern più significativi e fornire raccomandazioni immediate."
                  howToRead={[
                    'Il punteggio di confidenza indica l\'affidabilità statistica dell\'insight',
                    'Le etichette colorate mostrano se l\'insight è positivo (verde), negativo (rosso) o neutrale (blu)',
                    'Le azioni suggerite forniscono consigli pratici basati sui dati'
                  ]}
                  tips={[
                    'Concentrati sugli insight con confidenza superiore al 90%',
                    'Combina più insight per costruire strategie robuste',
                    'Verifica sempre gli insight con analisi dettagliate nelle altre sezioni'
                  ]}
                />
              </>
            )}
          </div>
        )}

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8 chart-animate-in">
            {isLoading ? (
              <TableSkeleton />
            ) : (
              <section>
              <div className="premium-chart-header">
                <div className="flex items-center space-x-3">
                  <div className="premium-icon-bg">
                    <Layers className="h-6 w-6 text-blue-400" />
                  </div>
                  <div>
                    <h2 className="premium-chart-title">Analisi per Categorie</h2>
                    <p className="premium-chart-subtitle">
                      Distribuzione dei gap in bucket predefiniti per identificare pattern e concentrazioni
                    </p>
                  </div>
                </div>
              </div>

              <ChartExplainer
                title="Analisi per Categorie"
                explanation="Questa analisi divide i gap in categorie (bucket) predefinite per identificare quali intervalli mostrano le migliori performance."
                howToRead={[
                  'La colonna Count mostra quanti gap appartengono a ciascuna categoria',
                  'La % Rosse indica la percentuale di chiusure in negativo per quella categoria',
                  'Le frecce verdi/rosse evidenziano trend significativi'
                ]}
                tips={[
                  'Categorie con alta % Rosse potrebbero essere favorevoli per strategie short',
                  'Cerca categorie con alto Count per avere maggiore significatività statistica',
                  'Combina l\'analisi di più bucket per affinare la tua strategia'
                ]}
                examples={[
                  'Se Gap 50-100% ha 70% di chiusure rosse, considera strategie short o evita posizioni long aggressive'
                ]}
              />

              <BucketAnalysisGrid data={filteredData} bias={filters.bias} />
            </section>
            )}
          </div>
        )}

        {/* Price Analysis Tab */}
        {activeTab === 'price' && (
          <div className="space-y-8 chart-animate-in">
            {isLoading ? (
              <ChartSkeleton />
            ) : (
              <section>
              <div className="premium-chart-header">
                <div className="flex items-center space-x-3">
                  <div className="premium-icon-bg success">
                    <TrendingUp className="h-6 w-6 text-green-400" />
                  </div>
                  <div>
                    <h2 className="premium-chart-title">Performance per Fascia di Prezzo</h2>
                    <p className="premium-chart-subtitle">
                      Analisi comparativa delle performance per diverse fasce di prezzo di apertura
                    </p>
                  </div>
                </div>
              </div>

              <ChartExplainer
                title="Performance per Fascia di Prezzo"
                explanation="Questo grafico mostra come le performance variano in base al prezzo di apertura del gap."
                howToRead={[
                  'Ogni barra rappresenta una fascia di prezzo',
                  'L\'altezza della barra indica il tasso di successo',
                  'Il colore indica se la performance è positiva (verde), neutra (giallo) o negativa (rosso)'
                ]}
                tips={[
                  'Gap in fasce di prezzo basse (<$2) tendono ad avere maggiore volatilità',
                  'Fasce con pochi dati potrebbero non essere statisticamente significative',
                  'Combina con l\'analisi Market Cap per una visione completa'
                ]}
              />

              <PerformancePerFasciaChart data={priceRangeData} />
            </section>
            )}
          </div>
        )}

        {/* Market Cap Tab */}
        {activeTab === 'marketcap' && (
          <div className="space-y-8 chart-animate-in">
            {isLoading ? (
              <ChartSkeleton />
            ) : (
              <section>
              <div className="premium-chart-header">
                <div className="flex items-center space-x-3">
                  <div className="premium-icon-bg warning">
                    <PieChart className="h-6 w-6 text-yellow-400" />
                  </div>
                  <div>
                    <h2 className="premium-chart-title">Analisi Market Cap</h2>
                    <p className="premium-chart-subtitle">
                      Distribuzione e metriche di performance per categoria di capitalizzazione
                    </p>
                  </div>
                </div>
              </div>

              <ChartExplainer
                title="Analisi Market Cap"
                explanation="Questa dashboard mostra come la capitalizzazione di mercato influenza le performance dei gap."
                howToRead={[
                  'Nano/Micro Cap: aziende piccole con alta volatilità',
                  'Mid Cap: equilibrio tra liquidità e potenziale',
                  'Large/Mega Cap: maggiore liquidità ma spike più contenuti'
                ]}
                tips={[
                  'Nano Cap richiedono stop loss più ampi per gestire la volatilità',
                  'Large Cap offrono esecuzioni migliori ma profitti potenziali minori',
                  'Diversifica tra diverse capitalizzazioni per bilanciare rischio/rendimento'
                ]}
              />

              <MarketCapAnalysisDashboard data={marketCapData} />
            </section>
            )}
          </div>
        )}

        {/* Correlations Tab */}
        {activeTab === 'correlations' && (
          <div className="space-y-8 chart-animate-in">
            {isLoading ? (
              <ChartSkeleton />
            ) : (
              <>
                <section>
                  <div className="premium-chart-header">
                    <div className="flex items-center space-x-3">
                      <div className="premium-icon-bg">
                        <Activity className="h-6 w-6 text-blue-400" />
                      </div>
                      <div>
                        <h2 className="premium-chart-title">Heatmap Gap vs Performance</h2>
                        <p className="premium-chart-subtitle">
                          Correlazione visiva tra dimensione del gap e metriche chiave
                        </p>
                      </div>
                    </div>
                  </div>

                  <ChartExplainer
                    title="Heatmap Gap vs Performance"
                    explanation="Questa heatmap mostra come diverse dimensioni di gap si correlano con le metriche di performance."
                    howToRead={[
                      'Le celle più calde (rosse) indicano valori alti per quella combinazione',
                      'Le celle più fredde (blu) indicano valori bassi',
                      'Il numero n= mostra quanti gap sono stati analizzati per quella cella'
                    ]}
                    tips={[
                      'Cerca pattern orizzontali per identificare quali metriche variano con il gap',
                      'Celle con n basso potrebbero essere meno affidabili statisticamente',
                      'Usa questa heatmap per identificare sweet spot dimensionali'
                    ]}
                  />

                  <GapPerformanceHeatmap data={heatmapData} />
                </section>

                <div className="premium-section-divider"></div>

                <section>
                  <div className="premium-chart-header">
                    <div className="flex items-center space-x-3">
                      <div className="premium-icon-bg">
                        <BarChart3 className="h-6 w-6 text-blue-400" />
                      </div>
                      <div>
                        <h2 className="premium-chart-title">Box Plot Comparativo</h2>
                        <p className="premium-chart-subtitle">
                          Distribuzione statistica delle metriche per categoria di market cap
                        </p>
                      </div>
                    </div>
                  </div>

                  <ChartExplainer
                    title="Box Plot Comparativo"
                    explanation="I box plot mostrano la distribuzione statistica delle metriche, evidenziando mediana, quartili e outlier."
                    howToRead={[
                      'La linea centrale nella scatola è la mediana (valore centrale)',
                      'I bordi della scatola rappresentano il 25° e 75° percentile',
                      'Le linee esterne mostrano il range tipico, i punti sono outlier'
                    ]}
                    tips={[
                      'Scatole più strette indicano maggiore consistenza',
                      'Molti outlier superiori indicano potenziale per grandi spike',
                      'Confronta le mediane tra categorie per identificare le migliori'
                    ]}
                    examples={[
                      'Se Micro Cap ha mediana spike 45% e Large Cap 25%, i Micro Cap offrono maggiore potenziale ma anche più rischio'
                    ]}
                  />

                  <ComparativeBoxPlot data={boxPlotData} />
                </section>

                <div className="premium-section-divider"></div>

                <section>
                  <div className="premium-chart-header">
                    <div className="flex items-center space-x-3">
                      <div className="premium-icon-bg">
                        <Activity className="h-6 w-6 text-blue-400" />
                      </div>
                      <div>
                        <h2 className="premium-chart-title">Matrice di Correlazione</h2>
                        <p className="premium-chart-subtitle">
                          Correlazioni statistiche tra le principali metriche di performance
                        </p>
                      </div>
                    </div>
                  </div>

                  <ChartExplainer
                    title="Matrice di Correlazione"
                    explanation="Questa matrice mostra quanto le diverse metriche sono correlate tra loro, aiutandoti a identificare relazioni nascoste."
                    howToRead={[
                      'Valori vicini a +1 indicano forte correlazione positiva',
                      'Valori vicini a -1 indicano forte correlazione negativa',
                      'Valori vicini a 0 indicano assenza di correlazione lineare'
                    ]}
                    tips={[
                      'Correlazioni forti possono aiutare a predire una metrica dall\'altra',
                      'Attenzione: correlazione non implica causalità',
                      'Usa queste informazioni per costruire indicatori compositi'
                    ]}
                    examples={[
                      'Se Gap% e Spike% hanno correlazione 0.7, gap più grandi tendono a produrre spike maggiori'
                    ]}
                  />

                  <CorrelationMatrixHeatmap data={correlationData} />
                </section>
              </>
            )}
          </div>
        )}

        {/* Setups Tab */}
        {activeTab === 'setups' && (
          <div className="space-y-8 chart-animate-in">
            {isLoading ? (
              <ChartSkeleton />
            ) : (
              <section>
                <div className="premium-chart-header">
                  <div className="flex items-center space-x-3">
                    <div className="premium-icon-bg success">
                      <Target className="h-6 w-6 text-green-400" />
                    </div>
                    <div>
                      <h2 className="premium-chart-title">Setup di Trading Ottimali</h2>
                      <p className="premium-chart-subtitle">
                        Identificazione automatica dei setup più performanti per bias long e short
                      </p>
                    </div>
                  </div>
                </div>

                <ChartExplainer
                  title="Setup di Trading Ottimali"
                  explanation="Questa sezione identifica automaticamente le configurazioni di trading con le migliori performance storiche nel tuo dataset."
                  howToRead={[
                    'Ogni card rappresenta un setup specifico con parametri ottimizzati',
                    'Success Rate mostra la percentuale di operazioni profittevoli',
                    'Avg Spike indica il movimento medio atteso',
                    'Count mostra quante volte si è verificato questo setup'
                  ]}
                  tips={[
                    'Setup Long Bias: configurazioni ottimali per posizioni long',
                    'Setup Short Bias: configurazioni che storicamente sottoperformano (ideali per short)',
                    'Hidden Gem: setup rari ma altamente profittevoli - richiede pazienza',
                    'Consistent: setup più prevedibili con minore varianza'
                  ]}
                  examples={[
                    'Un setup con 75% success rate e 100 occorrenze è più affidabile di uno con 80% ma solo 10 occorrenze',
                    'Usa Volume Monster per operazioni intraday con necessità di liquidità immediata'
                  ]}
                />

                <BestSetupsDashboard data={bestSetupsData} />
              </section>
            )}
          </div>
        )}
      </div>
    </div>
  );
};